export const COLUMN_STATE_STORAGE_KEYS = {
  promosGrid: "promosGrid",
  discountDetailsDiscounts: "discountDetailsDiscounts",
  discountDetailsClientDiscounts: "discountDetailsClientDiscounts",
};

/**
 * Properties we persist from AG Grid's columnState.
 *
 * Excludes sort/filter, which live in the URL, and the pivot/rowGroup/aggFunc
 * entries for features these grids don't use.
 *
 * `flex` is included, and has to be: Spellbook's DataGrid gives every column
 * without an explicit width `flex: 1`, so nearly every column here is a flex
 * column. AG Grid's applyColumnState only honours a saved `width` when the
 * entry's `flex` is null or absent *and* leaves the column's existing flex
 * untouched when the key is missing — so omitting it meant restoring a width
 * onto a still-flexing column, where the next flex pass immediately recomputed
 * it. Persisting flex as AG Grid reports it (a number, or null once a column is
 * no longer flexing) is what lets a saved width mean anything.
 */
const PERSISTED_COLUMN_PROPS = [
  "colId",
  "field",
  "width",
  "flex",
  "hide",
  "pinned",
];

/**
 * Filters columnState to only persistable properties.
 * @param {Array} columnState - AG Grid columnState array
 * @returns {Array|null} Filtered state or null if invalid
 */
export function filterColumnState(columnState) {
  if (!Array.isArray(columnState)) return null;

  return columnState
    .filter((col) => col && typeof col === "object")
    .map((col) => {
      const filtered = {};
      PERSISTED_COLUMN_PROPS.forEach((prop) => {
        if (col[prop] !== undefined) {
          filtered[prop] = col[prop];
        }
      });
      return filtered;
    })
    .filter((col) => col.colId || col.field);
}

/**
 * Gets column state from Redux store (backend-persisted preferences).
 * @param {Object} gridPreferences - Grid preferences from Redux state
 * @param {string} storageKey - Key from COLUMN_STATE_STORAGE_KEYS
 * @returns {Array|null} Saved column state or null
 */
export function getStoredColumnState(gridPreferences, storageKey) {
  if (!gridPreferences) return null;
  return gridPreferences[storageKey] || null;
}

/**
 * Collects the ids of every leaf column in a set of column definitions.
 *
 * Descends into column groups (`children`), because column state is keyed per
 * leaf column while a group def carries no colId/field of its own. Without the
 * recursion, wrapping columns in a group would read as "this grid has no
 * columns" and silently discard every saved preference.
 *
 * @param {Array} columnDefs - Column definitions, possibly containing groups
 * @returns {Set<string>}
 */
function collectLeafColumnIds(columnDefs) {
  return new Set(collectLeafColumnDefs(columnDefs).map(({ id }) => id));
}

/**
 * Flattens column definitions to their leaf columns, in display order.
 *
 * Paired with collectLeafColumnIds, which is the same traversal when only the
 * ids matter. This one keeps the definition alongside each id, because a column
 * added since the user last saved a layout has to fall back to the visibility
 * and pinning its own definition asks for.
 *
 * @param {Array} columnDefs - Column definitions, possibly containing groups
 * @param {Array<{id: string, def: Object}>} leaves - Accumulator
 * @returns {Array<{id: string, def: Object}>}
 */
function collectLeafColumnDefs(columnDefs, leaves = []) {
  columnDefs.forEach((col) => {
    if (!col || typeof col !== "object") return;
    if (Array.isArray(col.children)) {
      collectLeafColumnDefs(col.children, leaves);
      return;
    }
    const id = col.colId || col.field;
    if (id) leaves.push({ id, def: col });
  });
  return leaves;
}

/**
 * Merges saved column state with current column definitions.
 *
 * Saved state is user data that outlives the code that produced it, so the two
 * can always disagree. Both directions of that drift are handled here:
 *
 *   - A column dropped from the defs is filtered out, so nothing asks AG Grid
 *     to restore a column that no longer exists.
 *   - A column added to the defs is simply absent from the result. That's the
 *     point: AG Grid leaves a column with no state entry entirely alone, so a
 *     new column keeps the visibility and width from its own definition rather
 *     than inheriting anything from a state that predates it. It lands at the
 *     end of the column order, since `applyOrder` appends what the state
 *     doesn't mention.
 *
 * @param {Array} savedState - Saved column state from backend
 * @param {Array} currentColumnDefs - Current column definitions
 * @returns {Array|null} Merged state, or null when there is nothing to restore
 */
export function mergeColumnState(savedState, currentColumnDefs) {
  if (!savedState || !Array.isArray(savedState)) return null;
  if (!currentColumnDefs || !Array.isArray(currentColumnDefs)) return savedState;

  const currentColIds = collectLeafColumnIds(currentColumnDefs);

  // Only restore state for columns that still exist. Entries are guarded
  // individually because this state round-trips through JSONB — a malformed or
  // partially written row must not throw on the way back in.
  const merged = savedState.filter((col) => {
    if (!col || typeof col !== "object") return false;
    const id = col.colId || col.field;
    return id && currentColIds.has(id);
  });

  // Nothing survived — every saved column has since been renamed or removed.
  // Reported as "no saved state" so the caller skips the restore outright
  // instead of applying an empty one.
  return merged.length > 0 ? merged : null;
}

/**
 * Normalizes a `pinned` value from either a column state entry or a column
 * definition. State always reports "left" / "right" / null, but a definition may
 * also say `pinned: true`, which AG Grid reads as left.
 */
function normalizePinned(pinned) {
  if (pinned === true || pinned === "left") return "left";
  if (pinned === "right") return "right";
  return null;
}

/**
 * Converts a merged column state into an AG Grid `initialState` fragment.
 *
 * Why this exists: applying a saved layout with `api.applyColumnState()` can only
 * happen after the grid is mounted and has handed its api over, so the grid
 * paints its default column order first and visibly snaps to the saved one a
 * frame later. `initialState` is the pre-paint path — AG Grid folds it into the
 * column model while it is being built, so the first paint is already the user's
 * layout and there is nothing to flicker.
 *
 * Order, visibility and pinning are carried; width is not. `initialState` has no
 * way to say "leave this column's size alone" — providing the `columnSizing`
 * slice at all makes AG Grid default every column it doesn't mention to
 * `flex: null`, which would strip the flex Spellbook's DataGrid gives columns
 * without an explicit width and leave a newly added column stuck at a fixed size
 * for anyone with a saved layout. Widths still restore through the post-mount
 * applyColumnState, where they can be applied per column; a width settling one
 * frame late is not what the user sees, a reordered grid is.
 *
 * Every current column is named explicitly, in both cases for the same reason:
 * AG Grid's `defaultState` fills in whatever a column's entry leaves out, so a
 * column omitted from `hiddenColIds` is *shown*, not left alone. Columns the
 * saved layout predates therefore come from their own definitions and are
 * appended in definition order, matching where mergeColumnState leaves them.
 *
 * @param {Array|null} columnState - Merged column state (see mergeColumnState)
 * @param {Array} currentColumnDefs - Current column definitions
 * @returns {Object|null} A partial GridState, or null when there is nothing saved
 */
export function columnStateToInitialGridState(columnState, currentColumnDefs) {
  if (!Array.isArray(columnState) || columnState.length === 0) return null;

  const orderedColIds = [];
  const hiddenColIds = [];
  const leftColIds = [];
  const rightColIds = [];
  const seen = new Set();

  const add = (id, source) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    orderedColIds.push(id);
    if (source.hide) hiddenColIds.push(id);
    const pinned = normalizePinned(source.pinned);
    if (pinned === "left") leftColIds.push(id);
    else if (pinned === "right") rightColIds.push(id);
  };

  columnState.forEach((col) => {
    if (!col || typeof col !== "object") return;
    add(col.colId || col.field, col);
  });

  if (orderedColIds.length === 0) return null;

  const leafDefs = Array.isArray(currentColumnDefs)
    ? collectLeafColumnDefs(currentColumnDefs)
    : [];
  leafDefs.forEach(({ id, def }) => add(id, def));

  // partialColumnState keeps AG Grid from resetting the properties this fragment
  // says nothing about — sort in particular, which is the URL's to own, and flex.
  return {
    partialColumnState: true,
    columnOrder: { orderedColIds },
    columnVisibility: { hiddenColIds },
    columnPinning: { leftColIds, rightColIds },
  };
}
